import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import LayersIcon from '@mui/icons-material/Layers';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { productionKeys } from './queryKeys';
import {
  listPressConfig,
  listSeparationProfiles,
  deleteSeparationProfile,
} from './productionApi';
import type {
  SeparationProfileSummary,
  SeparationScopeType,
} from './productionTypes';
import SeparationProfileEditor from './SeparationProfileEditor';

const PRESS = 'P15';

type ScopeFilter = 'all' | SeparationScopeType | 'unapproved';

function scopeChipColour(
  scope: SeparationScopeType,
): 'primary' | 'success' | 'info' | 'warning' | 'default' {
  if (scope === 'default') return 'primary';
  if (scope === 'artwork') return 'success';
  if (scope === 'stock_code') return 'info';
  if (scope === 'substrate') return 'warning';
  return 'default';
}

function scopeLabel(scope: SeparationScopeType): string {
  if (scope === 'customer') return 'client';
  if (scope === 'stock_code') return 'stock code';
  if (scope === 'substrate') return 'raw material';
  if (scope === 'artwork') return 'sku';
  return scope;
}

function describeScope(p: SeparationProfileSummary): { primary: string; secondary: string } {
  if (p.scope_type === 'default') return { primary: '—', secondary: '(applies to everything)' };
  if (p.scope_type === 'customer')
    return { primary: p.scope_value ?? '—', secondary: 'all artwork for this client' };
  if (p.scope_type === 'stock_code')
    return { primary: p.scope_value ?? '—', secondary: 'all artwork on this stock code' };
  if (p.scope_type === 'substrate')
    return { primary: p.scope_value ?? '—', secondary: 'all artwork on this raw material' };
  // artwork: scope_value is "customer|customer_ref"
  const [customer, ref] = (p.scope_value ?? '').split('|');
  return { primary: customer || '—', secondary: ref || '—' };
}

export default function SequencesView(): JSX.Element {
  const queryClient = useQueryClient();
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>('all');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<{ profileId: number | null } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const listKey = productionKeys.separationProfiles({ press: PRESS });
  const profilesQuery = useQuery({
    queryKey: listKey,
    queryFn: () => listSeparationProfiles({ press: PRESS }),
  });
  const pressConfigQuery = useQuery({
    queryKey: productionKeys.pressConfig(),
    queryFn: listPressConfig,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteSeparationProfile,
    onError: (e: any) => setError(e?.response?.data?.error ?? e?.message ?? 'Delete failed'),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['production', 'separation-profiles'] }),
  });

  const profiles = profilesQuery.data ?? [];

  const filtered = useMemo(() => {
    let result = profiles;
    if (scopeFilter === 'unapproved') {
      result = result.filter((p) => !p.approved);
    } else if (scopeFilter !== 'all') {
      result = result.filter((p) => p.scope_type === scopeFilter);
    }
    const s = search.trim().toLowerCase();
    if (s) {
      result = result.filter((p) => {
        const { primary, secondary } = describeScope(p);
        return (
          primary.toLowerCase().includes(s) ||
          secondary.toLowerCase().includes(s) ||
          (p.scope_value ?? '').toLowerCase().includes(s) ||
          (p.notes ?? '').toLowerCase().includes(s)
        );
      });
    }
    return result;
  }, [profiles, scopeFilter, search]);

  const handleDelete = (p: SeparationProfileSummary) => {
    if (p.scope_type === 'default') return;
    const { primary, secondary } = describeScope(p);
    if (
      !window.confirm(
        `Delete ${scopeLabel(p.scope_type)} profile for ${primary}${
          secondary && secondary !== '—' ? ` / ${secondary}` : ''
        }?`,
      )
    )
      return;
    deleteMutation.mutate(p.id);
  };

  const p15 = pressConfigQuery.data?.find((c) => c.press_code === PRESS);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(135deg, rgba(203,96,21,0.05) 0%, rgba(203,96,21,0.01) 100%)',
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              display: 'inline-flex',
            }}
          >
            <LayersIcon />
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Separation Sequences
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
              Which colour or coating sits in each unit on{' '}
              <strong>{PRESS}</strong>
              {p15 ? ` (${p15.ink_units} ink + ${p15.coater_units} coaters)` : ''}.
              Resolution cascade (highest priority first):{' '}
              <strong>SKU → stock code → raw material → client → default</strong>.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setEditing({ profileId: null })}
            data-testid="new-sequence-btn"
          >
            New profile
          </Button>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filter bar */}
      <Paper
        elevation={0}
        sx={{ p: { xs: 2, md: 2 }, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            select
            label="Scope"
            value={scopeFilter}
            onChange={(e) => setScopeFilter(e.target.value as ScopeFilter)}
            size="small"
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="default">Default</MenuItem>
            <MenuItem value="customer">Client</MenuItem>
            <MenuItem value="substrate">Raw material</MenuItem>
            <MenuItem value="stock_code">Stock code</MenuItem>
            <MenuItem value="artwork">SKU</MenuItem>
            <MenuItem value="unapproved">Unapproved only</MenuItem>
          </TextField>
          <TextField
            label="Search customer, ref, or notes"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            fullWidth
          />
        </Stack>
      </Paper>

      {/* Profiles table */}
      <Paper
        elevation={0}
        sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Scope</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Value</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Detail</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Source</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">
                Approved
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Last updated</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {profilesQuery.isLoading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={20} />
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No profiles match these filters. Click <strong>New profile</strong> to create one.
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((p) => {
                const { primary, secondary } = describeScope(p);
                return (
                  <TableRow key={p.id} hover data-testid={`sequence-row-${p.id}`}>
                    <TableCell>
                      <Chip
                        size="small"
                        label={scopeLabel(p.scope_type)}
                        color={scopeChipColour(p.scope_type)}
                        variant={p.scope_type === 'default' ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}>
                      {primary}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}>
                      {secondary}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {p.source}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {p.approved ? (
                        <Chip size="small" label="Approved" color="success" variant="outlined" />
                      ) : (
                        <Chip size="small" label="Draft" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {new Date(p.updated_at).toLocaleString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        aria-label="Edit"
                        onClick={() => setEditing({ profileId: p.id })}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label="Delete"
                        disabled={p.scope_type === 'default'}
                        onClick={() => handleDelete(p)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Paper>

      {editing && (
        <SeparationProfileEditor
          profileId={editing.profileId}
          press={PRESS}
          onClose={() => setEditing(null)}
        />
      )}
    </Box>
  );
}
